import { useState, useRef } from 'react';
import { Upload, FileImage, Loader2, CheckCircle2, AlertTriangle, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
    ApiError,
    scan as scanApi,
    shifts as shiftsApi,
    type ScanResult,
    type ShiftUpsertPayload,
} from '@/lib/api';

export default function ScanForm() {
    const [files, setFiles] = useState<File[]>([]);
    const [previews, setPreviews] = useState<string[]>([]);
    const [scanning, setScanning] = useState(false);
    const [result, setResult] = useState<ScanResult | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    function handleFiles(selected: FileList | null) {
        if (!selected) return;
        const newFiles = Array.from(selected).filter((f) => f.type.startsWith('image/'));
        if (newFiles.length === 0) {
            toast.error('Please select image files');
            return;
        }
        setFiles(newFiles);
        setPreviews(newFiles.map((f) => URL.createObjectURL(f)));
        setResult(null);
    }

    async function handleScan() {
        if (files.length === 0) return;
        setScanning(true);
        try {
            const data = await scanApi.extract(files);
            setResult(data);
            toast.success('Form scanned successfully');
        } catch (e) {
            const msg = e instanceof ApiError ? e.message : 'Scan failed';
            toast.error(msg);
        } finally {
            setScanning(false);
        }
    }

    async function handleSubmit() {
        if (!result) return;
        setSubmitting(true);
        try {
            const shiftId = crypto.randomUUID();
            const now = new Date().toISOString();
            const payload: ShiftUpsertPayload = {
                id: shiftId,
                app_slug: 'dashboard',
                shift_data: {
                    shiftDetails: {
                        site: result.shift_details.site ?? '',
                        date: result.shift_details.date ?? '',
                        shiftType: result.shift_details.shiftType ?? 'day',
                        operatorName: result.shift_details.operatorName ?? '',
                        rigId: result.shift_details.rigId ?? '',
                        rigType: result.shift_details.rigType ?? '',
                        startTime: result.shift_details.startTime ?? '',
                        endTime: result.shift_details.endTime ?? '',
                        location: result.shift_details.location ?? '',
                        workAreas: [],
                        patternIds: result.shift_details.patternId
                            ? [result.shift_details.patternId]
                            : [],
                    },
                    drillHoles: result.drill_holes.map((h, i) => ({
                        id: `${shiftId}-h${i + 1}`,
                        holeNumber: h.holeNumber ?? `H${String(i + 1).padStart(3, '0')}`,
                        location: result.shift_details.location ?? '',
                        depth: h.depth ?? 0,
                        diameter: h.diameter ?? 165,
                        angle: h.angle ?? 90,
                        status: 'completed',
                        drillingTimeSeconds: (h.drillingTimeMinutes ?? 0) * 60,
                        workArea: '',
                        patternId: result.shift_details.patternId ?? '',
                        notes: h.notes ?? '',
                    })),
                    consumables: result.consumables.map((c, i) => ({
                        id: `${shiftId}-c${i + 1}`,
                        type: c.type ?? 'drill-bit',
                        name: c.diameter ? `${c.diameter} ${c.type}` : '',
                        manufacturer: c.manufacturer ?? '',
                        serialNumber: c.serialNumber ?? '',
                        metresDrilled: c.metresDrilled ?? 0,
                        bitDiameter: parseInt(c.diameter ?? '0') || undefined,
                        linkedHoleIds: [],
                    })),
                    delays: result.delays.map((d, i) => ({
                        id: `${shiftId}-d${i + 1}`,
                        delayType: d.delayType ?? 'other',
                        description: d.notes ?? '',
                        timeSeconds: (d.timeHours ?? 0) * 3600 + (d.timeMinutes ?? 0) * 60,
                        notes: d.notes ?? '',
                    })),
                    notes: result.shift_notes,
                    createdAt: now,
                },
                status: 'submitted',
                submitted_at: now,
            };
            await shiftsApi.upsert(payload);
            toast.success('Shift submitted successfully');
            setFiles([]);
            setPreviews([]);
            setResult(null);
        } catch (e) {
            const msg = e instanceof ApiError ? e.message : 'Submit failed';
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    }

    function reset() {
        setFiles([]);
        setPreviews([]);
        setResult(null);
    }

    return (
        <div className='space-y-6 max-w-4xl'>
            <div>
                <h1 className='text-2xl font-bold'>Scan Paper Form</h1>
                <p className='text-muted-foreground text-sm mt-1'>
                    Upload a photo of a completed shift log paper form. AI will extract the data for
                    review before submission.
                </p>
            </div>

            {/* Upload area */}
            {!result && (
                <Card>
                    <CardContent className='pt-6'>
                        <div
                            className='border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary/50 transition-colors cursor-pointer'
                            onClick={() => inputRef.current?.click()}
                        >
                            <input
                                ref={inputRef}
                                type='file'
                                accept='image/*'
                                multiple
                                className='hidden'
                                onChange={(e) => handleFiles(e.target.files)}
                            />
                            <Upload className='h-10 w-10 text-muted-foreground mx-auto mb-3' />
                            <p className='text-sm font-medium'>Click to upload form images</p>
                            <p className='text-xs text-muted-foreground mt-1'>
                                Front and back of form (max 4 images, JPEG/PNG)
                            </p>
                        </div>

                        {previews.length > 0 && (
                            <div className='mt-4 flex gap-3 flex-wrap'>
                                {previews.map((src, i) => (
                                    <div key={i} className='relative'>
                                        <img
                                            src={src}
                                            alt={`Page ${i + 1}`}
                                            className='h-32 rounded border border-border object-cover'
                                        />
                                        <Badge className='absolute top-1 left-1 text-[10px]'>
                                            Page {i + 1}
                                        </Badge>
                                    </div>
                                ))}
                            </div>
                        )}

                        {files.length > 0 && (
                            <div className='mt-4 flex gap-2'>
                                <Button onClick={handleScan} disabled={scanning}>
                                    {scanning ? (
                                        <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                                    ) : (
                                        <FileImage className='h-4 w-4 mr-2' />
                                    )}
                                    {scanning ? 'Scanning...' : 'Extract Data'}
                                </Button>
                                <Button variant='outline' onClick={reset}>
                                    Clear
                                </Button>
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Results */}
            {result && (
                <div className='space-y-4'>
                    {/* Confidence badge */}
                    <div className='flex items-center gap-2'>
                        {result.confidence.overall === 'high' ? (
                            <Badge className='bg-green-500/15 text-green-500 border-green-500/30'>
                                <CheckCircle2 className='h-3 w-3 mr-1' /> High Confidence
                            </Badge>
                        ) : result.confidence.overall === 'medium' ? (
                            <Badge className='bg-amber-500/15 text-amber-500 border-amber-500/30'>
                                <AlertTriangle className='h-3 w-3 mr-1' /> Medium Confidence
                            </Badge>
                        ) : (
                            <Badge className='bg-red-500/15 text-red-500 border-red-500/30'>
                                <AlertTriangle className='h-3 w-3 mr-1' /> Low Confidence
                            </Badge>
                        )}
                        {result.confidence.issues.length > 0 && (
                            <span className='text-xs text-muted-foreground'>
                                Issues: {result.confidence.issues.join(', ')}
                            </span>
                        )}
                    </div>

                    {/* Shift Details */}
                    <Card>
                        <CardHeader className='pb-3'>
                            <CardTitle className='text-sm'>Shift Details</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className='grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm'>
                                <div>
                                    <span className='text-muted-foreground'>Date:</span>{' '}
                                    {result.shift_details.date}
                                </div>
                                <div>
                                    <span className='text-muted-foreground'>Shift:</span>{' '}
                                    {result.shift_details.shiftType}
                                </div>
                                <div>
                                    <span className='text-muted-foreground'>Operator:</span>{' '}
                                    {result.shift_details.operatorName}
                                </div>
                                <div>
                                    <span className='text-muted-foreground'>Rig:</span>{' '}
                                    {result.shift_details.rigId} ({result.shift_details.rigType})
                                </div>
                                <div>
                                    <span className='text-muted-foreground'>Site:</span>{' '}
                                    {result.shift_details.site}
                                </div>
                                <div>
                                    <span className='text-muted-foreground'>Location:</span>{' '}
                                    {result.shift_details.location}
                                </div>
                                <div>
                                    <span className='text-muted-foreground'>Start:</span>{' '}
                                    {result.shift_details.startTime}
                                </div>
                                <div>
                                    <span className='text-muted-foreground'>End:</span>{' '}
                                    {result.shift_details.endTime}
                                </div>
                                <div>
                                    <span className='text-muted-foreground'>Pattern:</span>{' '}
                                    {result.shift_details.patternId || '—'}
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Consumables */}
                    {result.consumables.length > 0 && (
                        <Card>
                            <CardHeader className='pb-3'>
                                <CardTitle className='text-sm'>
                                    Consumables ({result.consumables.length})
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <table className='w-full text-sm'>
                                    <thead>
                                        <tr className='text-xs text-muted-foreground border-b'>
                                            <th className='text-left py-1'>Type</th>
                                            <th className='text-left'>Manufacturer</th>
                                            <th className='text-left'>Serial</th>
                                            <th className='text-left'>Dia.</th>
                                            <th className='text-right'>Metres</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {result.consumables.map((c, i) => (
                                            <tr key={i} className='border-b border-border/50'>
                                                <td className='py-1'>{c.type}</td>
                                                <td>{c.manufacturer}</td>
                                                <td className='font-mono text-xs'>
                                                    {c.serialNumber}
                                                </td>
                                                <td>{c.diameter}</td>
                                                <td className='text-right'>{c.metresDrilled}m</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </CardContent>
                        </Card>
                    )}

                    {/* Delays */}
                    {result.delays.length > 0 && (
                        <Card>
                            <CardHeader className='pb-3'>
                                <CardTitle className='text-sm'>
                                    Delays ({result.delays.length})
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <table className='w-full text-sm'>
                                    <thead>
                                        <tr className='text-xs text-muted-foreground border-b'>
                                            <th className='text-left py-1'>Type</th>
                                            <th className='text-left'>Duration</th>
                                            <th className='text-left'>Notes</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {result.delays.map((d, i) => (
                                            <tr key={i} className='border-b border-border/50'>
                                                <td className='py-1'>{d.delayType}</td>
                                                <td>
                                                    {d.timeHours}h {d.timeMinutes}m
                                                </td>
                                                <td className='text-muted-foreground'>{d.notes}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </CardContent>
                        </Card>
                    )}

                    {/* Drill Holes */}
                    {result.drill_holes.length > 0 && (
                        <Card>
                            <CardHeader className='pb-3'>
                                <CardTitle className='text-sm'>
                                    Drill Holes ({result.drill_holes.length})
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <table className='w-full text-sm'>
                                    <thead>
                                        <tr className='text-xs text-muted-foreground border-b'>
                                            <th className='text-left py-1'>Hole</th>
                                            <th className='text-right'>Depth</th>
                                            <th className='text-right'>Dia.</th>
                                            <th className='text-right'>Angle</th>
                                            <th className='text-right'>Time</th>
                                            <th className='text-left pl-3'>Notes</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {result.drill_holes.map((h, i) => (
                                            <tr key={i} className='border-b border-border/50'>
                                                <td className='py-1 font-mono'>{h.holeNumber}</td>
                                                <td className='text-right'>{h.depth}m</td>
                                                <td className='text-right'>{h.diameter}mm</td>
                                                <td className='text-right'>{h.angle}°</td>
                                                <td className='text-right'>
                                                    {h.drillingTimeMinutes}min
                                                </td>
                                                <td className='text-left pl-3 text-muted-foreground'>
                                                    {h.notes}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <p className='text-xs text-muted-foreground mt-2'>
                                    Total: {result.drill_holes.length} holes,{' '}
                                    {result.drill_holes
                                        .reduce((s, h) => s + (h.depth ?? 0), 0)
                                        .toFixed(1)}
                                    m
                                </p>
                            </CardContent>
                        </Card>
                    )}

                    {/* Notes */}
                    {result.shift_notes && (
                        <Card>
                            <CardHeader className='pb-3'>
                                <CardTitle className='text-sm'>Shift Notes</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className='text-sm'>{result.shift_notes}</p>
                            </CardContent>
                        </Card>
                    )}

                    {/* Actions */}
                    <div className='flex gap-3'>
                        <Button onClick={handleSubmit} disabled={submitting}>
                            {submitting ? (
                                <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                            ) : (
                                <Send className='h-4 w-4 mr-2' />
                            )}
                            {submitting ? 'Submitting...' : 'Submit Shift'}
                        </Button>
                        <Button variant='outline' onClick={reset}>
                            Scan Another
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
